import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useServiceChargePeriod, useServiceChargeAssignments, useServiceChargeMutations, useServiceChargePeriods } from '@/hooks/serviceCharge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EVEN_SHARE_WEIGHT_LABEL } from '@/services/compensation/serviceChargeService';

interface ServiceChargeEmployeeAssignmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodId: string;
  periodLabel: string;
}

export const ServiceChargeEmployeeAssignment = ({
  open,
  onOpenChange,
  periodId,
  periodLabel,
}: ServiceChargeEmployeeAssignmentProps) => {
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const { data: period, isLoading: periodLoading } = useServiceChargePeriod(periodId, open);
  const { data: existingAssignments, isLoading: assignmentsLoading } = useServiceChargeAssignments(periodId, open);
  const { data: periods } = useServiceChargePeriods();
  const { assignEmployeeWeights, copyWeightsFromPeriod } = useServiceChargeMutations();

  const UNASSIGNED = '__unassigned__';
  const isReadOnly = period?.status === 'finalized';
  const isEvenMode = (period as any)?.distribution_mode === 'even';
  const evenWeightId = period?.weights.find(w => w.weight_label === EVEN_SHARE_WEIGHT_LABEL)?.id
    ?? period?.weights[0]?.id;

  const { data: employees, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, fte, weekly_hours')
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  useEffect(() => {
    if (existingAssignments) {
      const assignmentMap: Record<string, string> = {};
      existingAssignments.forEach((a) => {
        assignmentMap[a.profile_id] = a.weight_id;
      });
      setAssignments(assignmentMap);
    }
  }, [existingAssignments]);

  const originalProfileIds = useMemo(
    () => new Set((existingAssignments ?? []).map((a) => a.profile_id)),
    [existingAssignments]
  );

  const handleSave = async () => {
    const assignmentList = Object.entries(assignments)
      .filter(([_, weightId]) => weightId && weightId !== UNASSIGNED)
      .map(([profileId, weightId]) => ({
        profile_id: profileId,
        weight_id: weightId,
      }));

    const keptProfileIds = new Set(assignmentList.map((a) => a.profile_id));
    const removedProfileIds = Array.from(originalProfileIds).filter(
      (id) => !keptProfileIds.has(id)
    );

    await assignEmployeeWeights.mutateAsync({
      periodId,
      assignments: assignmentList,
      removedProfileIds,
    });
    onOpenChange(false);
  };

  const handleClearAll = () => {
    const cleared: Record<string, string> = {};
    Object.keys(assignments).forEach((id) => {
      cleared[id] = UNASSIGNED;
    });
    setAssignments(cleared);
  };

  const handleCopyFromPeriod = async (sourcePeriodId: string) => {
    await copyWeightsFromPeriod.mutateAsync({
      sourcePeriodId,
      targetPeriodId: periodId,
    });
  };

  const previousPeriods = periods?.filter(p => p.id !== periodId && new Date(p.period_start) < new Date(period?.period_start || ''));

  const isLoading = periodLoading || assignmentsLoading || employeesLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEvenMode ? 'Assign Employees' : 'Assign Employee Weights'} - {periodLabel}
          </DialogTitle>
          <DialogDescription>
            {isEvenMode
              ? 'Select the employees who share this period\u2019s staff allocation equally'
              : 'Assign weight categories to employees for this service charge period'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <>
            {isEvenMode && !evenWeightId && (
              <Alert variant="destructive">
                <AlertDescription>
                  This period has no distribution category yet. Re-save the period in the edit
                  dialog to enable employee assignment.
                </AlertDescription>
              </Alert>
            )}
            {previousPeriods && previousPeriods.length > 0 && (
              <div className="flex items-center justify-between gap-2 pb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Copy from:</span>
                  <Select onValueChange={handleCopyFromPeriod} disabled={isReadOnly}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select period..." />
                  </SelectTrigger>
                  <SelectContent>
                    {previousPeriods.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.period_label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                  </Select>
                </div>
                {!isReadOnly && Object.values(assignments).some((v) => v && v !== UNASSIGNED) && (
                  <Button variant="outline" size="sm" onClick={handleClearAll}>
                    <X className="h-4 w-4 mr-2" />
                    Clear all
                  </Button>
                )}
              </div>
            )}
            {(!previousPeriods || previousPeriods.length === 0) && !isReadOnly && Object.values(assignments).some((v) => v && v !== UNASSIGNED) && (
              <div className="flex justify-end pb-4">
                <Button variant="outline" size="sm" onClick={handleClearAll}>
                  <X className="h-4 w-4 mr-2" />
                  Clear all
                </Button>
              </div>
            )}

            <div className="flex-1 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>FTE</TableHead>
                    <TableHead>Weekly Hours</TableHead>
                    <TableHead>{isEvenMode ? 'Included' : 'Weight Category'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees?.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>
                        {employee.first_name} {employee.last_name}
                      </TableCell>
                      <TableCell>{employee.fte || '-'}</TableCell>
                      <TableCell>{employee.weekly_hours || '-'}</TableCell>
                      <TableCell>
                        {isEvenMode ? (
                          <Checkbox
                            checked={
                              !!assignments[employee.id] &&
                              assignments[employee.id] !== UNASSIGNED
                            }
                            disabled={isReadOnly || !evenWeightId}
                            onCheckedChange={(checked) =>
                              setAssignments({
                                ...assignments,
                                [employee.id]: checked && evenWeightId ? evenWeightId : UNASSIGNED,
                              })
                            }
                            aria-label={`Include ${employee.first_name} ${employee.last_name}`}
                          />
                        ) : (
                        <Select
                          value={assignments[employee.id] || ''}
                          onValueChange={(value) =>
                            setAssignments({ ...assignments, [employee.id]: value })
                          }
                          disabled={isReadOnly}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Select weight..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNASSIGNED}>
                              <span className="text-muted-foreground">Unassigned</span>
                            </SelectItem>
                            {period?.weights.map((weight) => (
                              <SelectItem key={weight.id} value={weight.id}>
                                {weight.weight_label}
                                {` (${weight.weight_multiplier}×)`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnly && (
          <Button onClick={handleSave} disabled={assignEmployeeWeights.isPending || isLoading}>
            {assignEmployeeWeights.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Save Assignments
              </>
            )}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
